import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { WhatsAppIcon, WHATSAPP_LINK } from "@/data/brand";
import { useI18n } from "@/i18n";

const Wordmark = () => (
  <a
    href="#top"
    data-testid="nav-logo"
    className="flex items-center gap-3 group"
    aria-label="Rexxpons Network"
  >
    <span className="relative flex items-center justify-center w-9 h-9">
      <span className="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 opacity-90 blur-[6px] group-hover:opacity-100 transition-opacity duration-500" />
      <span className="relative flex items-center justify-center w-9 h-9 rounded-lg border border-white/15 bg-[#0d1526]/80 font-display font-bold text-sm tracking-tighter text-white">
        R<span className="text-blue-400">x</span>
      </span>
    </span>
    <span className="font-display text-lg font-semibold tracking-tight text-white">
      Rexxpons
      <span className="text-white/40 font-light"> Network</span>
    </span>
  </a>
);

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <div
      data-testid="lang-toggle"
      className="flex items-center rounded-full border border-white/12 bg-white/[0.03] p-0.5 text-xs font-semibold"
    >
      {["it", "en"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className={`px-3 py-1.5 rounded-full uppercase tracking-wide transition-colors duration-300 ${
            lang === code
              ? "bg-blue-600 text-white"
              : "text-white/50 hover:text-white"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-[#0a0f1a]/75 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <Wordmark />

        <nav className="hidden md:flex items-center gap-10">
          {t.nav.links.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-link-${item.key}`}
              className="text-sm text-white/60 hover:text-white transition-colors duration-300 relative group"
            >
              {item.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-400" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white hover:border-blue-400/60 hover:bg-white/[0.06] transition-colors duration-300"
          >
            <WhatsAppIcon className="w-4 h-4 text-emerald-400" />
            <span className="hidden sm:inline">{t.nav.whatsapp}</span>
          </a>
        </div>
      </div>
    </motion.header>
  );
};
